import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, Upload, Link2, Loader2, ImagePlus, CheckCircle2, AlertTriangle, Pencil, Trash2, X, Eye, EyeOff } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SapCodesImportDialog from "./SapCodesImportDialog";
import SapRangeDialog from "./SapRangeDialog";
import { fileToDataUrl } from "@/lib/images";
import ImageCropDialog from "@/components/ImageCropDialog";
import ImageGalleryEditor from "@/components/ImageGalleryEditor";

// Downscale a file to <=maxPx wide/tall and return a JPEG data URL.
export default function FlowerImages() {
  const [flowers, setFlowers] = useState([]);
  const [search, setSearch] = useState("");
  const [visFilter, setVisFilter] = useState("all"); // all | visible | hidden
  const [savingId, setSavingId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [unmatched, setUnmatched] = useState([]); // {filename, image_url, suggestions:[{flower_id,flower_name}]}
  const [urlDialog, setUrlDialog] = useState(null); // flower obj
  const [urlValue, setUrlValue] = useState("");
  const [detail, setDetail] = useState(null); // {flower, price, order_multiple, metcash_ref}
  const [sapCodesOpen, setSapCodesOpen] = useState(false);
  const [sapRangeOpen, setSapRangeOpen] = useState(false);
  const [savingDetail, setSavingDetail] = useState(false);
  const [cropQueue, setCropQueue] = useState(null); // {files, onDone}
  const [selected, setSelected] = useState(new Set()); // flower ids selected for bulk delete
  const [confirmDelete, setConfirmDelete] = useState(null); // {mode:'single'|'bulk', flower?}
  const [deleting, setDeleting] = useState(false);
  const singleInputs = useRef({});
  const bulkInput = useRef(null);

  const load = () => adminApi.flowers().then(setFlowers).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => flowers.filter((f) => f.name.toLowerCase().includes(search.toLowerCase()))
      .filter((f) => visFilter === "all" ? true : visFilter === "hidden" ? !!f.hidden : !f.hidden),
    [flowers, search, visFilter]
  );
  const withImage = flowers.filter((f) => f.image_url).length;
  const hiddenCount = flowers.filter((f) => f.hidden).length;

  const setVisibility = async (ids, hidden) => {
    try {
      const r = await adminApi.setFlowersVisibility(ids, hidden);
      const set = new Set(ids);
      setFlowers((list) => list.map((f) => (set.has(f.id) ? { ...f, hidden } : f)));
      toast.success(`${r.updated_count} product${r.updated_count === 1 ? "" : "s"} ${hidden ? "hidden from stores" : "now visible to stores"}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const applyImage = async (flowerId, dataUrl) => {
    setSavingId(flowerId);
    try {
      const updated = await adminApi.setFlowerImage(flowerId, dataUrl);
      setFlowers((list) => list.map((f) => (f.id === flowerId ? updated : f)));
      toast.success("Image updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingId(null);
    }
  };

  const onSingleFile = async (flower, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setCropQueue({ files: [file], onDone: (dataUrl) => applyImage(flower.id, dataUrl) });
  };

  const saveUrl = async () => {
    if (!urlValue.trim()) return toast.error("Enter an image URL");
    await applyImage(urlDialog.id, urlValue.trim());
    setUrlDialog(null); setUrlValue("");
  };

  const onBulkFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBulkBusy(true);
    try {
      const items = [];
      for (const f of files) {
        try { items.push({ filename: f.name, image_url: await fileToDataUrl(f) }); }
        catch { /* skip unreadable */ }
      }
      const res = await adminApi.bulkFlowerImages(items);
      const m = res.matched?.length || 0;
      const u = res.unmatched?.length || 0;
      const errs = res.errors?.length || 0;
      toast.success(`${m} matched by filename${u ? `, ${u} need review` : ""}${errs ? `, ${errs} skipped` : ""}`);
      setUnmatched(res.unmatched || []);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBulkBusy(false);
    }
  };

  const assignUnmatched = async (u, flowerId) => {
    await applyImage(flowerId, u.image_url);
    setUnmatched((list) => list.filter((x) => x.filename !== u.filename));
  };

  const saveDetail = async () => {
    const price = parseFloat(detail.price);
    const om = parseInt(detail.order_multiple || "0", 10);
    if (!(price >= 0)) return toast.error("Enter a valid price");
    setSavingDetail(true);
    try {
      const images = detail.images || [];
      const saved = await adminApi.upsertFlower({ ...detail.flower, price, order_multiple: isNaN(om) ? 0 : om, metcash_ref: (detail.metcash_ref || "").trim(), sap_material_code: (detail.sap_material_code || "").trim(), event_keys: detail.flower.event_keys, images, image_url: images[0] || detail.flower.image_url });
      setFlowers((list) => list.map((x) => (x.id === saved.id ? saved : x)));
      toast.success("Details saved");
      setDetail(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSavingDetail(false); }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const allFilteredSelected = filtered.length > 0 && filtered.every((f) => selected.has(f.id));
  const toggleSelectAll = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allFilteredSelected) filtered.forEach((f) => next.delete(f.id)); else filtered.forEach((f) => next.add(f.id));
    return next;
  });
  const SelectAll = ({ testid }) => (
    <label className="flex cursor-pointer items-center gap-2 text-xs font-semibold text-[#28282E]">
      <input type="checkbox" data-testid={testid} checked={allFilteredSelected} onChange={toggleSelectAll} disabled={filtered.length === 0} className="h-4 w-4 accent-[#28282E]" />
      Select all {visFilter === "all" ? "" : visFilter} ({filtered.length})
    </label>
  );

  const undoToast = (msg, ids) => toast.success(msg, {
    duration: 10000,
    action: { label: "Undo", onClick: async () => {
      try { const r = await adminApi.restoreFlowers(ids); toast.success(`${r.restored} flower(s) restored`); load(); }
      catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    } },
  });

  const runDelete = async () => {
    setDeleting(true);
    try {
      if (confirmDelete.mode === "single") {
        const id = confirmDelete.flower.id;
        await adminApi.deleteFlower(id);
        setFlowers((list) => list.filter((f) => f.id !== id));
        setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
        undoToast("Flower deleted", [id]);
      } else {
        const ids = [...selected];
        const res = await adminApi.bulkDeleteFlowers(ids);
        setFlowers((list) => list.filter((f) => !selected.has(f.id)));
        clearSelection();
        undoToast(`${res.deleted_count} flower(s) deleted`, ids);
      }
      setConfirmDelete(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setDeleting(false); }
  };

  return (
    <div data-testid="admin-flowers">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#28282E]">Flower Images</h1>
          <p className="text-sm text-[#28282E]/70">{withImage} of {flowers.length} flowers have photos. Upload one, or drop many and we'll match by filename.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#28282E]/55" />
            <Input data-testid="flowers-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search flowers…" className="w-56 pl-9" />
          </div>
          <div data-testid="flowers-visibility-filter" className="flex rounded-full border border-slate-200 bg-white p-0.5 text-xs font-semibold">
            {[["all", `All (${flowers.length})`], ["visible", `Visible (${flowers.length - hiddenCount})`], ["hidden", `Hidden (${hiddenCount})`]].map(([k, label]) => (
              <button key={k} data-testid={`flowers-filter-${k}`} onClick={() => setVisFilter(k)}
                className={`rounded-full px-3 py-1.5 transition-colors ${visFilter === k ? "bg-[#28282E] text-white" : "text-[#28282E]/70 hover:bg-slate-100"}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="rounded-full border border-slate-200 bg-white px-3 py-1.5"><SelectAll testid="flowers-select-all" /></div>
          <input ref={bulkInput} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={onBulkFiles} data-testid="flowers-bulk-input" />
          <Button data-testid="flowers-bulk-upload-button" onClick={() => bulkInput.current?.click()} disabled={bulkBusy} className="bg-[#28282E] text-white hover:bg-[#1F1F24]">
            {bulkBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Bulk Upload
          </Button>
          <Button data-testid="flowers-sap-codes-button" variant="outline" onClick={() => setSapCodesOpen(true)} className="border-slate-200">SAP codes</Button>
          <Button data-testid="flowers-sap-range-button" onClick={() => setSapRangeOpen(true)} className="bg-[#D46117] text-white hover:bg-[#B8520F]">IGA SAP range</Button>
          <SapRangeDialog open={sapRangeOpen} onClose={() => setSapRangeOpen(false)} onDone={load} />
          <SapCodesImportDialog open={sapCodesOpen} onClose={() => setSapCodesOpen(false)} onDone={load} />
        </div>
      </div>

      {selected.size > 0 && (
        <div data-testid="flowers-bulk-action-bar" className="sticky top-2 z-30 mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#EBBAD1] bg-[#F7E3EC]/95 px-5 py-3 shadow-md backdrop-blur-sm">
          <div className="flex items-center gap-4">
            <span className="text-sm font-semibold text-[#28282E]">{selected.size} flower{selected.size > 1 ? "s" : ""} selected</span>
            <SelectAll testid="flowers-bar-select-all" />
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="flowers-bulk-clear-button" variant="outline" size="sm" onClick={clearSelection} className="border-[#28282E]/20 text-[#28282E]">
              <X className="mr-1.5 h-4 w-4" /> Clear
            </Button>
            <Button data-testid="flowers-bulk-hide-button" variant="outline" size="sm" onClick={() => { const ids = [...selected]; setVisibility(ids, true); clearSelection(); }} className="border-[#28282E]/20 text-[#28282E]">
              <EyeOff className="mr-1.5 h-4 w-4" /> Hide selected
            </Button>
            <Button data-testid="flowers-bulk-unhide-button" variant="outline" size="sm" onClick={() => { const ids = [...selected]; setVisibility(ids, false); clearSelection(); }} className="border-[#28282E]/20 text-[#28282E]">
              <Eye className="mr-1.5 h-4 w-4" /> Unhide selected
            </Button>
            <Button data-testid="flowers-bulk-delete-button" size="sm" onClick={() => setConfirmDelete({ mode: "bulk" })} className="bg-red-600 text-white hover:bg-red-700">
              <Trash2 className="mr-1.5 h-4 w-4" /> Delete selected
            </Button>
          </div>
        </div>
      )}

      {unmatched.length > 0 && (
        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="flowers-unmatched-panel">
          <div className="mb-3 flex items-center gap-2 text-amber-800">
            <AlertTriangle className="h-5 w-5" />
            <h2 className="font-serif text-lg font-semibold">{unmatched.length} image(s) need manual matching</h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {unmatched.map((u) => (
              <div key={u.filename} data-testid={`unmatched-${u.filename}`} className="rounded-xl border border-amber-200 bg-white p-3">
                <div className="flex gap-3">
                  <img src={u.image_url} alt={u.filename} className="h-16 w-16 rounded-lg object-cover" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium text-[#28282E]">{u.filename}</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(u.suggestions || []).map((s) => (
                        <button key={s.flower_id} data-testid={`assign-suggestion-${s.flower_id}`} onClick={() => assignUnmatched(u, s.flower_id)}
                          className="rounded-full bg-[#EBBAD1] px-2 py-0.5 text-[11px] font-semibold text-[#28282E] hover:bg-[#EBBAD1]">
                          {s.flower_name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <Select onValueChange={(v) => assignUnmatched(u, v)}>
                  <SelectTrigger className="mt-2 h-8" data-testid={`assign-select-${u.filename}`}><SelectValue placeholder="Assign to another flower…" /></SelectTrigger>
                  <SelectContent>{flowers.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {filtered.map((f) => (
          <div key={f.id} data-testid={`flower-image-card-${f.id}`} className={`overflow-hidden rounded-2xl border bg-white ${selected.has(f.id) ? "border-red-400 ring-2 ring-red-200" : "border-slate-200"} ${f.hidden ? "opacity-70" : ""}`}>
            <div className="relative h-40 bg-slate-100">
              {f.image_url ? (
                <img src={f.image_url} alt={f.name} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center text-slate-300"><ImagePlus className="h-8 w-8" /></div>
              )}
              <label className="absolute left-2 top-2 flex h-6 w-6 cursor-pointer items-center justify-center rounded-md bg-white/90 shadow-sm ring-1 ring-slate-200">
                <input type="checkbox" data-testid={`flower-select-${f.id}`} checked={selected.has(f.id)} onChange={() => toggleSelect(f.id)} className="h-4 w-4 accent-red-600" />
              </label>
              {f.image_url && <CheckCircle2 className="absolute right-2 top-2 h-5 w-5 rounded-full bg-white text-emerald-500" />}
              {f.hidden && (
                <span data-testid={`flower-hidden-badge-${f.id}`} className="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-[#28282E]/85 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                  <EyeOff className="h-3 w-3" /> Hidden
                </span>
              )}
              {savingId === f.id && <div className="absolute inset-0 grid place-items-center bg-white/60"><Loader2 className="h-6 w-6 animate-spin text-[#28282E]" /></div>}
            </div>
            <div className="p-3">
              <div className="truncate text-sm font-semibold text-[#28282E]">{f.name}</div>
              <div className="text-xs text-[#28282E]/55">{f.category}</div>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] font-medium text-[#28282E]/70" data-testid={`flower-details-line-${f.id}`}>
                <span>${(f.price || 0).toFixed(2)}</span>
                <span>×{f.order_multiple || f.pack_size || 1}/carton</span>
                {f.metcash_ref ? <span>Ref {f.metcash_ref}</span> : <span className="italic text-[#28282E]/40">No Metcash ref</span>}
                {f.sap_material_code ? <span> · SAP {f.sap_material_code}</span> : <span className="italic text-[#28282E]/40"> · No SAP code</span>}
              </div>
              <input ref={(el) => (singleInputs.current[f.id] = el)} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => onSingleFile(f, e)} data-testid={`flower-file-input-${f.id}`} />
              <div className="mt-3 flex gap-2">
                <button data-testid={`flower-details-${f.id}`} onClick={() => setDetail({ flower: f, price: String(f.price ?? ""), order_multiple: String(f.order_multiple || f.pack_size || 1), metcash_ref: f.metcash_ref || "", sap_material_code: f.sap_material_code || "", images: (f.images && f.images.length ? f.images : (f.image_url ? [f.image_url] : [])) })}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#28282E] py-1.5 text-xs font-semibold text-white hover:bg-[#1F1F24]">
                  <Pencil className="h-3.5 w-3.5" /> Details
                </button>
                <button data-testid={`flower-upload-${f.id}`} onClick={() => singleInputs.current[f.id]?.click()} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#EBBAD1] py-1.5 text-xs font-semibold text-[#28282E] hover:bg-[#EBBAD1]">
                  <Upload className="h-3.5 w-3.5" /> Replace
                </button>
                <button data-testid={`flower-url-${f.id}`} onClick={() => { setUrlDialog(f); setUrlValue(f.image_url?.startsWith("http") ? f.image_url : ""); }} className="grid h-8 w-9 place-items-center rounded-lg border border-slate-200 text-[#28282E]/70 hover:border-[#EBBAD1] hover:text-[#28282E]">
                  <Link2 className="h-4 w-4" />
                </button>
                <button data-testid={`flower-hide-${f.id}`} title={f.hidden ? "Show to stores" : "Hide from stores"} onClick={() => setVisibility([f.id], !f.hidden)}
                  className={`grid h-8 w-9 place-items-center rounded-lg border ${f.hidden ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-slate-200 text-[#28282E]/70 hover:border-[#28282E]/40 hover:text-[#28282E]"}`}>
                  {f.hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
                <button data-testid={`flower-delete-${f.id}`} onClick={() => setConfirmDelete({ mode: "single", flower: f })} className="grid h-8 w-9 place-items-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="col-span-full py-16 text-center text-sm text-[#28282E]/55">No flowers match your search.</p>}
      </div>

      <Dialog open={!!urlDialog} onOpenChange={(v) => !v && setUrlDialog(null)}>
        <DialogContent className="max-w-md" data-testid="flower-url-modal">
          <DialogHeader><DialogTitle className="font-serif">Set image by URL</DialogTitle></DialogHeader>
          <p className="text-sm text-[#28282E]/70">{urlDialog?.name}</p>
          <div><Label className="text-xs">Image URL (https://)</Label><Input data-testid="flower-url-input" value={urlValue} onChange={(e) => setUrlValue(e.target.value)} className="mt-1" placeholder="https://…" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUrlDialog(null)}>Cancel</Button>
            <Button data-testid="flower-url-save" onClick={saveUrl} className="bg-[#28282E] text-white hover:bg-[#1F1F24]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-md" data-testid="flower-details-modal">
          {detail && (<>
            <DialogHeader><DialogTitle className="font-serif">{detail.flower.name}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Price ($)</Label><Input type="number" min="0" step="0.01" data-testid="flower-detail-price" value={detail.price} onChange={(e) => setDetail({ ...detail, price: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Order multiple (units/carton)</Label><Input type="number" min="1" data-testid="flower-detail-multiple" value={detail.order_multiple} onChange={(e) => setDetail({ ...detail, order_multiple: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Metcash IBS ref</Label><Input data-testid="flower-detail-ref" value={detail.metcash_ref} onChange={(e) => setDetail({ ...detail, metcash_ref: e.target.value })} className="mt-1" placeholder="e.g. 841045" /></div>
              <div><Label className="text-xs">SAP material code</Label><Input data-testid="flower-detail-sap" value={detail.sap_material_code} onChange={(e) => setDetail({ ...detail, sap_material_code: e.target.value })} className="mt-1" placeholder="e.g. 100234" /></div>
              <div className="col-span-2"><Label className="text-xs">Images (first = main; stores can swipe through all)</Label><div className="mt-1"><ImageGalleryEditor testid="flower-gallery" value={detail.images} onChange={(images) => setDetail({ ...detail, images })} /></div></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetail(null)}>Cancel</Button>
              <Button data-testid="flower-detail-save" onClick={saveDetail} disabled={savingDetail} className="bg-[#28282E] text-white hover:bg-[#1F1F24]">{savingDetail ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save</Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      {cropQueue && <ImageCropDialog files={cropQueue.files} onDone={cropQueue.onDone} onCancel={() => setCropQueue(null)} />}

      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent className="max-w-md" data-testid="flower-delete-modal">
          <DialogHeader><DialogTitle className="font-serif text-red-700">Delete {confirmDelete?.mode === "bulk" ? `${selected.size} flowers` : "flower"}?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#28282E]/70">
            {confirmDelete?.mode === "bulk"
              ? `This permanently removes ${selected.size} flower(s) from the catalogue. They will no longer appear in the store ordering view.`
              : <>This permanently removes <strong>{confirmDelete?.flower?.name}</strong> from the catalogue. It will no longer appear in the store ordering view.</>}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)} disabled={deleting}>Cancel</Button>
            <Button data-testid="flower-delete-confirm" onClick={runDelete} disabled={deleting} className="bg-red-600 text-white hover:bg-red-700">
              {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />} Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
